import AsyncStorage from '@react-native-async-storage/async-storage';
import type { RefObject } from 'react';
import type { Location as BGLocation, MotionChangeEvent } from 'react-native-background-geolocation';
import { appendCoordinateToPath, getLocationAccuracyMeters, getLocationTimestampMs, isPlausiblePathAppend, toTrailPoint } from './backgroundTracking';
import { RECORDING_STORAGE_KEYS } from './recordingSession';
import { diag, diagInc, locationCounters } from './diagnostics';

// ─── Sniff-time detection ─────────────────────────────────────────
// A dog is considered to be sniffing when it has stopped or nearly
// stopped moving.  We detect this via the GPS-reported speed rather
// than the SDK's onMotionChange events, because our anti-kill config
// (disableStopDetection, stopTimeout: 0, isMoving: true) makes the
// SDK's motion-change unreliable.

/** Speed (m/s) below which the dog is considered to be sniffing/
 *  stationary.  0.5 m/s ≈ 1.8 km/h — well below a typical walking
 *  pace of 1.0–3.0 m/s. */
export const SNIFF_SPEED_THRESHOLD_MPS = 0.5;

/** Minimum duration (seconds) a low-speed period must last to be
 *  counted as sniff time.  Filters out GPS noise and brief pauses
 *  (checking phone, waiting at a kerb) that aren't true sniffing. */
export const MIN_SNIFF_DURATION_S = 3;

type Helpers = {
  isRecordingRef: RefObject<boolean>;
  setCurrentLocation: (c: { latitude: number; longitude: number } | null) => void;
  setAccuracy: (a: number | undefined) => void;
  setCoordinates: (coords: any) => void; // dispatcher
  coordinatesRef: RefObject<any[]>;
  lastAcceptedGpsTimestamp: RefObject<number>;
  lastLocationTimestamp: RefObject<number>;
  setElevation?: (n: number) => void;
  maxElevationRef?: RefObject<number>;
  setMaxElevation?: (n: number) => void;
  setSpeed?: (n: number) => void;
  maxSpeedRef?: RefObject<number>;
  setMaxSpeed?: (n: number) => void;
  saveBackup?: (coords: any[], sniffTime?: number) => void;
  lastBackupPointsRef?: RefObject<number>;
  lastBackupTimeRef?: RefObject<number>;
  stopStartTimeRef?: RefObject<number | null>;
  sniffTimeRef?: RefObject<number>;
  setSniffTime?: (n: number) => void;
};

// Throttle UI updates to prevent map from being overwhelmed
// Recording still captures all points, but UI only updates every N ms
const UI_UPDATE_THROTTLE_MS = 2500; // Update map/UI every 1.5 seconds
const lastUIUpdateRef: { current: number } = { current: 0 };

export function processBGLocation(location: BGLocation, h: Helpers) {
  try {
    const locationAccuracy = getLocationAccuracyMeters(location);

    // console.log('BG location received. timestamp=', getLocationTimestampMs(location), 'accuracy=', locationAccuracy, 'isSample=', location?.sample);

    const coord = toTrailPoint(location);

    // console.log('Parsed coordinate:', coord);

    if (!coord) return;

    // Always track timestamp for watchdog monitoring (not for filtering)
    const gpsTs = getLocationTimestampMs(location);
    if (gpsTs) h.lastAcceptedGpsTimestamp.current = gpsTs;
    h.lastLocationTimestamp.current = Date.now();

    // Diagnostics: count SDK deliveries for the one-tap dump.
    diagInc('onLocationEvents');
    locationCounters.lastLocationAccuracy = locationAccuracy;
    locationCounters.lastLocationAt = Date.now();
    if (locationCounters.onLocationEvents === 1) diag('first_on_location', `acc=${locationAccuracy}`);

    const isSample = !!(location as any).sample;

    // Record to path with stale-location filtering.
    // appendCoordinateToPath now uses isPlausiblePathAppend to reject
    // iOS stale cache re-deliveries (poor accuracy + large jump from last
    // path point) while accepting Android's legitimately delayed but
    // high-accuracy updates.
    if (h.isRecordingRef.current && !isSample) {
      // SDK guidance: stationary-monitor samples are not persisted.
      // TrailPoints carry their own accuracy/isSample, but pass the
      // options through so the map-fallback path (plain lat/lon coord)
      // still gets jump filtering.
      try {
        appendCoordinateToPath(h.setCoordinates, coord, h.coordinatesRef.current, {
          accuracy: locationAccuracy,
          isSample,
        });
      } catch (e) {
        // fallback
        appendCoordinateToPath(h.setCoordinates, coord, undefined, {
          accuracy: locationAccuracy,
          isSample,
        });
      }
    }

    // Throttle UI updates to prevent map from being overwhelmed
    const now = Date.now();
    const shouldUpdateUI = (now - lastUIUpdateRef.current) >= UI_UPDATE_THROTTLE_MS;
    
    if (shouldUpdateUI || !h.isRecordingRef.current) {
      lastUIUpdateRef.current = now;

      // ── Marker jump protection ──────────────────────────────────────
      // Don't move the map marker to a stale/implausible position.
      // On iOS, sample (cached) locations and poor-accuracy locations
      // that are far from the last good path point indicate a stale
      // Core Location cache re-delivery — typically the recording start
      // position.  Moving the marker there causes the visual "jump to
      // start" bug.
      const lastGoodCoord = h.coordinatesRef.current.length > 0
        ? h.coordinatesRef.current[h.coordinatesRef.current.length - 1]
        : undefined;
      const markerPlausible = isPlausiblePathAppend(coord, lastGoodCoord, locationAccuracy, isSample);

      if (markerPlausible && !isSample) {
        h.setCurrentLocation(coord);
      } else if (!markerPlausible) {
        diagInc('rejectedStaleMarkers');
      }

      h.setAccuracy(locationAccuracy);
    }

    if (h.isRecordingRef.current && shouldUpdateUI) {
      // elevation
      try {
        const altitude = (location as any)?.coords?.altitude;
        if (typeof altitude === 'number' && h.setElevation && h.maxElevationRef && h.setMaxElevation) {
          const currentElevation = Math.max(0, altitude);
          h.setElevation(currentElevation);
          if (currentElevation > h.maxElevationRef.current) {
            h.maxElevationRef.current = currentElevation;
            h.setMaxElevation(currentElevation);
            AsyncStorage.setItem(RECORDING_STORAGE_KEYS.MAX_ELEVATION, currentElevation.toString()).catch(() => {});
          }
        }
      } catch (e) {
        // ignore
      }

      // speed (display)
      try {
        const rawSpeed = (location as any)?.coords?.speed;
        if (typeof rawSpeed === 'number' && rawSpeed > 0 && h.setSpeed && h.maxSpeedRef && h.setMaxSpeed) {
          const currentSpeed = rawSpeed * 3.6;
          h.setSpeed(currentSpeed);
          if (currentSpeed > h.maxSpeedRef.current) {
            h.maxSpeedRef.current = currentSpeed;
            h.setMaxSpeed(currentSpeed);
            AsyncStorage.setItem(RECORDING_STORAGE_KEYS.MAX_SPEED, currentSpeed.toString()).catch(() => {});
          }
        }
      } catch (e) {
        // ignore
      }
    }

    // ── Sniff-time detection (speed-based) ─────────────────────────
    // Runs on EVERY location update (not throttled by shouldUpdateUI)
    // so that speed transitions are detected promptly.  sniffTimeRef
    // tracks "accumulated completed sniff periods" only — the timer
    // in the screen component adds the ongoing period for display.
    if (h.isRecordingRef.current) {
      try {
        const rawSpeed = (location as any)?.coords?.speed;
        const speedValid = typeof rawSpeed === 'number' && rawSpeed >= 0; // -1 means invalid on iOS

        if (speedValid && h.stopStartTimeRef && h.sniffTimeRef && h.setSniffTime) {
          if (rawSpeed < SNIFF_SPEED_THRESHOLD_MPS) {
            // Dog is (nearly) stationary — start or continue sniff period
            if (h.stopStartTimeRef.current === null) {
              h.stopStartTimeRef.current = Date.now();
            }
          } else {
            // Dog is moving again — end sniff period if one was active
            if (h.stopStartTimeRef.current !== null) {
              const elapsedS = Math.floor((Date.now() - h.stopStartTimeRef.current) / 1000);
              if (elapsedS >= MIN_SNIFF_DURATION_S) {
                h.sniffTimeRef.current += elapsedS;
                h.setSniffTime(h.sniffTimeRef.current);
              }
              h.stopStartTimeRef.current = null;
            }
          }
        }
      } catch (e) {
        // ignore — sniff time is non-critical
      }
    }
  } catch (err) {
    console.warn('processBGLocation error', err);
  }
}

export function makeMotionHandler(h: Helpers) {
  return (event: MotionChangeEvent) => {
    console.log('MotionChangeEvent', event);
    if (!h.isRecordingRef.current) return;

    if (!event.isMoving) {
      // Device stopped — save a backup of coordinates.
      // NOTE: We do NOT set stopStartTimeRef here.  Sniff-time
      // detection is handled entirely by processBGLocation's speed-
      // based logic (which runs on every location update).  The
      // SDK's motion-change events are unreliable due to our anti-
      // kill config (disableStopDetection, stopTimeout: 0), so we
      // don't use them for sniff time tracking.
      try {
        if (h.coordinatesRef.current.length > 0 && h.saveBackup && h.lastBackupTimeRef && h.lastBackupPointsRef) {
          h.saveBackup(h.coordinatesRef.current, h.sniffTimeRef?.current);
          h.lastBackupTimeRef.current = Date.now();
          h.lastBackupPointsRef.current = h.coordinatesRef.current.length;
        }
      } catch (e) { /* swallow */ }
    }
    // When isMoving: true, we don't need to end a sniff period here
    // because processBGLocation's speed-based detection already
    // handles that on every location update.
  };
}
